import type { TImageResize } from '@ds/components'

const DEFAULT_QUALITY = 75

/** audio-web's `imageLoader`, kept identical so both stacks hit the same cached derivatives. */
export const buildImageSrc = (src: string, width?: number, quality?: number) =>
    `${src}${width ? `@${width}` : ''}?qlt=${quality || DEFAULT_QUALITY}&fmt=webp`

/** For endpoints that generate the image from query params (`/genres/image?top=…`). */
export const buildQueryImageSrc = (src: string, width?: number, quality?: number) => {
    const url = new URL(src)

    url.searchParams.set('resize', `@${width || ''}?qlt=${quality || DEFAULT_QUALITY}&fmt=webp`)

    return url.toString()
}

// audio-web's `next.config` `deviceSizes`, where 480 lets a 240px slot at 2x DPR fetch 480 instead
// of jumping to 640. A `vw` sizes is measured against the first of these.
export const IMAGE_DEVICE_WIDTHS = [480, 640, 750, 828, 1080, 1200, 1920, 2048, 3840]

// `next/image`'s default `imageSizes` in front of them — together they are its `allSizes`.
export const IMAGE_WIDTHS = [16, 32, 48, 64, 96, 128, 256, 384, ...IMAGE_DEVICE_WIDTHS]

/**
 * Handed to `Cover` / `ResponsiveImage` as `resize` so a caller only chooses `sizes`: the DS must not
 * know the product's resize scheme, and repeating the ladder per call site is how candidates get lost.
 *
 * Annotated with the DS's own type on purpose. It is the repo's only core → ds import, and it earns
 * that: dropping a field here without it compiles to 52 errors spread across features and screens,
 * none of them pointing at this file. `import type` erases at build, and the direction is the allowed
 * one — `ds` imports nothing from `core`, so there is no cycle.
 */
export const IMAGE_RESIZE_SPEC: TImageResize = {
    widths: IMAGE_WIDTHS,
    deviceWidths: IMAGE_DEVICE_WIDTHS,
    buildSrc: buildImageSrc,
}
